import { useTheme } from "@emotion/react";
import { Close, Search } from "@mui/icons-material";
import { Grid, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useReducer, useState } from "react";
import { CONTACTS_APIS, DETAILS_APIS, MASTER_DATA_APIS } from "../../../../common/apis";
import Form, { FormActions } from "../../../../components/Form";
import FormFooter from "../../../../components/FormFooter";
import useFetch from "../../../../hooks/useFetch";
import { useToast } from "../../../../rootContext";
import appResources from "../../../../app-resources/en/Resources.json";
import NewDataTable, { NewDataGridAction } from "../../../../components/NewDataTable";

export default function SearchCustomer({
  actions,
  contactId,
  rowSelected,
  selectedUnit,
  propId
}) {
  const curTheme = useTheme();
  const { showWarningToast } = useToast();
  const [customersData, setCustomersData] = useState([]);
  const { get, post } = useFetch();

  const [data, dispatch] = useReducer(SearchFluidsFilterReducer, searchFilterDefaults);
  const [allProducts, setAllProducts] = useState([]);

  const tblOptions = {
    columns: GetColumns(actions),
    dataSource: customersData,
    getRowId: (originalRow) => originalRow?.CustomerId,
    enableColumnActions: false,
    sx: { height: "50dvh"},

    cellStylingHandler: "productCommonCellColour",

    rowsSelected: function (rows) {
      rowSelected && rowSelected(rows[0]);
    },

    muiTableBodyCellProps: ({ cell, column, row, table }) => {
      return {
        sx:
          cell.column.columnDef.accessorKey === "Product" && row.original.Product ? {
            backgroundColor: (row?.original?.ProductColour + "!important") || "",
            color: "white",
            py: 0.25,
            border: "0.5px solid #d0d0d0",
          } : {
            py: 0.25,
            border: "0.5px solid #d0d0d0",
          }
      }
    },

    muiTableBodyProps: {
      sx: (theme) => ({
        '& tr:nth-of-type(odd):not([data-selected="true"]):not([data-pinned="true"]) > td':
          { backgroundColor: "inherit" },
        '& tr:nth-of-type(odd):not([data-selected="true"]):not([data-pinned="true"]):hover > td':
          { backgroundColor: "inherit" },
        '& tr:nth-of-type(even):not([data-selected="true"]):not([data-pinned="true"]) > td':
          { backgroundColor: "#e2f2ff" },
        // backgroundColor: "inherit",//
        '& tr:nth-of-type(even):not([data-selected="true"]):not([data-pinned="true"]):hover > td':
          { backgroundColor: "#e2f2ff" },
      }),
    },
  };

  const formActions = {
    clear: function () {
      dispatch({
        payload: {
          ...searchFilterDefaults,
          _key: Date.now(),
        },
        type: FormActions.RESET_FORM,
      });
    },
    search: async function () {
      if (contactId) {
        const customersData = await post(CONTACTS_APIS.CUSTOMERS_FOR_CONTACT, {
          ...data,
          ContactId: contactId,
          ExcludeScoped: (propId === 2) ? true : false,
          UseStartsWith: data?.UseStartsWith === true ? true : false,
          ShowAll: true,
        });
        if (customersData.length > 0) {
          setCustomersData(customersData);
        } else {
          showWarningToast(appResources.NO_MATCHING_CUSTOMERS_WERE_FOUND_MSG);
          setCustomersData(customersData);
        }
        //incase of call from management
      } else {
        const resp = await post(DETAILS_APIS.SEARCH_CUSTOMERS, {
          ...data,
          // ContactId: contactId,
          Town: null,
          UseStartsWith: data?.UseStartsWith == true ? true : false,
          UnarchivedOnly: null,
          Parent: null,
        });

        const customersData = resp?.filter(i => i?.CustomerId !== selectedUnit?.CustomerId);
        if (customersData?.length > 0) {
          setCustomersData(customersData);
        } else {
          showWarningToast(appResources.NO_MATCHING_CUSTOMERS_WERE_FOUND_MSG);
          setCustomersData(customersData);
        }
      }
    },
  };

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (data?.Customer) {
          formActions?.search();
        }
      }
    }, [data]);

  useEffect(() => {
    document.addEventListener("keyup", handleKeyPress);
    return () => {
      document.removeEventListener("keyup", handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    (async function () {
      const response = await get(`${MASTER_DATA_APIS.GET_BY_TYPES}?types=Products`);
      //Products with AllProducts added at 0 index
      const allProd = JSON.parse(JSON.stringify(response.Products));
      if (allProd) {
        allProd.unshift({
          ProductId: 0,
          Product: "All Products",
          Colour: "",
        });
      }
      setAllProducts(allProd || {});
    })();

    if (selectedUnit) {
      dispatch({
        payload: {
          ...searchFilterDefaults,
          ProductId: selectedUnit?.ProductId,
          Customer: selectedUnit?.Customer || "",
        },
        type: FormActions.LOAD_DATA,
      });
    }
  }, [selectedUnit]);

  return (
    <>
      <Grid container minWidth={800} spacing={1}>
        <Grid item xs={12}>
          <Form
            sections={GetFormSections(allProducts)}
            data={data}
            dispatch={dispatch}
            key={data && data._key}
          />
        </Grid>
        <Grid item xs={12} sx={{ mt: -5 }}>
          <FormFooter
            buttons={GetFormButtons(data, formActions)}
            hideDivider
            gmt={0}
          />
        </Grid>
        <Grid item xs={12} sx={{ mt: curTheme.spacing(2), mb: curTheme.spacing(2) }}>
          <Typography variant="subtitle2">
            Total Records: <b> {customersData && customersData.length}</b>
          </Typography>
          <NewDataTable {...tblOptions} />
        </Grid>
      </Grid>
    </>
  );
}

const GetFormButtons = (data, { clear, search }) => [
  {
    variant: "outlined",
    label: "Clear",
    onClick: clear,
    startIcon: <Close />,
  },
  {
    variant: "contained",
    label: "Search",
    onClick: search,
    disabled: !(data?.Customer),
    startIcon: <Search />,
  },
];

function GetColumns(actions) {
  return [
    {
      header: "Actions",
      size: 60,
      hide: !actions || !actions.length,
      Cell: function ({ row }) {
        return (actions || []).map(function (ac, i) {
          return (
            <NewDataGridAction
              row={row?.original}
              onClick={ac.action}
              label={ac.label}
            // startIcon={ac.startIcon}
            />
          );
        });
      },
    },
    {
      header: "Product",
      accessorKey: "Product",
      size: 150,
    },
    {
      header: "Customer",
      accessorKey: "Customer",
      size: 500,
    },
    {
      header: "Archived",
      accessorKey: "ProductArchived",
      // valueFormatter: ({ value }) => (value ? "Yes" : "No"),
      size: 100,
    },
  ];
}

function GetFormSections(allProducts) {
  return [
    {
      xsCol: 12,
      fields: [
        {
          xsCol: 2,
          name: "ProductId",
          label: "Product",
          type: "dropdown",
          required: true,
          selectOptions: (allProducts || []).map((x) => ({
            value: x.ProductId,
            text: x.Product,
          })),
        },
        {
          xsCol: 4,
          name: "Customer",
          label: "Customer",
        },
        {
          xsCol: 4,
          name: "UseStartsWith",
          label: "Starts With Search",
          type: "switch",
          alignSelf: "flex-end",
        },
      ],
    },
  ];
}

const searchFilterDefaults = {
  ProductId: 0,
  UseStartsWith: true,
};

function SearchFluidsFilterReducer(state, action) {
  if (action.type === FormActions.INPUT_CHANGED) {
    return { ...state, [action.payload.name]: action.payload.value };
  }
  if (action.type === FormActions.LOAD_DATA) {
    return { ...action.payload, _key: Date.now() };
  }
  if (action.type === FormActions.RESET_FORM) {
    return { ...action.payload };
  }
  return state;
}
